import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { getSafeAccountRedirectPath } from '#pplx/frontend/apps/ask/auth/switchAccount.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { useIsUserLoggedIn, useIsUserLoggedInLoading } from '#pplx/frontend/auth/userHooks.js';
import { getWindowOrigin, toPublicWebUrl } from '#pplx/frontend/common/location.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
const RequestAccessModalDynamic = Dynamic(async () => {
    const { RequestAccessModal } = await import('#pplx/frontend/apps/ask/access/RequestAccessModal.js');
    return { default: RequestAccessModal };
});
// Consumers remount while their gated query refetches; reopening the modal with
// `replace: true` would evict the live one, whose onClose navigates away from
// the gated URL. Remounts must reuse the open gate instead.
let openLoggedInGateKey;
export function useRequestAccess({ redirectBasePath = '/search', subject = 'thread', } = {}) {
    const isUserLoggedIn = useIsUserLoggedIn();
    const isUserLoggedInLoading = useIsUserLoggedInLoading();
    const { openModal } = useModalContext();
    const { $t } = useIntl();
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({
        enabled: true,
    });
    const handleRequestAccess = useCallback((uuidOrSlug, options) => {
        const gateKey = `${subject}:${uuidOrSlug}`;
        if (isUserLoggedIn && openLoggedInGateKey === gateKey) {
            return;
        }
        const isWebsite = subject === 'website';
        const isAsset = subject === 'asset';
        const isSpace = subject === 'space';
        logger.info(isSpace
            ? 'Project access not allowed'
            : isWebsite || isAsset
                ? 'Asset access not allowed'
                : 'Thread access not allowed', {
            ...(isSpace ? { uuidOrSlugLength: uuidOrSlug.length } : { uuidOrSlug }),
            isUserLoggedIn,
        });
        // Collapse duplicate slashes: with redirectBasePath '/' the template
        // would yield a protocol-relative '//<slug>' that resolves to an
        // external origin.
        const fallbackRedirectUrl = toPublicWebUrl(`${redirectBasePath}/${uuidOrSlug}`.replace(/\/{2,}/g, '/'));
        const redirectUrl = getSafeSameOriginRedirectUrl(options?.overrideRedirectUrl, fallbackRedirectUrl);
        if (isUserLoggedIn) {
            openLoggedInGateKey = gateKey;
            openModal(RequestAccessModalDynamic, {
                redirectPath: getSafeAccountRedirectPath(redirectUrl),
                uuidOrSlug,
                subject,
                onClose: () => {
                    // A replace-mode open of a *different* gate overwrites the key
                    // before this eviction close runs; only clear our own key.
                    if (openLoggedInGateKey === gateKey) {
                        openLoggedInGateKey = undefined;
                    }
                    options?.closeCallback?.();
                },
            }, { replace: true });
        }
        else {
            const loginGateContent = (() => {
                if (isWebsite) {
                    return {
                        title: $t({
                            defaultMessage: 'This website is private',
                            description: 'Login gate title shown when a visitor tries to access a private website',
                            id: '54bdd2ec1b',
                        }),
                        description: $t({
                            defaultMessage: 'Sign in if you are the owner of this website, or to request access',
                            description: 'Login gate description shown when a visitor tries to access a private website',
                            id: '7b9cb6b20e',
                        }),
                    };
                }
                if (isAsset) {
                    return {
                        title: $t({
                            defaultMessage: 'This artifact is private',
                            description: 'Login gate title shown when a visitor tries to access a private artifact',
                            id: 'a1c3faa829',
                        }),
                        description: $t({
                            defaultMessage: 'Sign in if you are the owner of this artifact, or to request access',
                            description: 'Login gate description shown when a visitor tries to access a private artifact',
                            id: 'd3ed5ece4a',
                        }),
                    };
                }
                if (isSpace) {
                    return {
                        title: $t({
                            defaultMessage: 'This project is private',
                            description: 'Login gate title shown when a visitor tries to access a private project',
                            id: 'be73cbb52a',
                        }),
                        description: $t({
                            defaultMessage: 'Sign in if you are the owner of this project, or to request access',
                            description: 'Login gate description shown when a visitor tries to access a private project',
                            id: '6f04c6d5aa',
                        }),
                    };
                }
                return {
                    title: $t({
                        defaultMessage: 'This session is private',
                        description: 'Login gate title shown when a visitor tries to access a private thread',
                        id: '8c6f174e5b',
                    }),
                    description: $t({
                        defaultMessage: 'Sign in if you are the owner of this session, or to request access',
                        description: 'Login gate description shown when a visitor tries to access a private thread',
                        id: 'ebe9f92673',
                    }),
                };
            })();
            openVisitorLoginUpsell({
                origin: ModalOrigin.REQUEST_ACCESS_LOGIN_GATE,
                title: loginGateContent.title,
                description: loginGateContent.description,
                sheetModalVariant: 'centered-sheet',
                overrideRedirectUrl: redirectUrl,
                closeCallback: options?.closeCallback,
            });
            // Sidecar builds short-circuit the visitor gate into window.open of the
            // sign-in page — no modal ever renders, so gates drawn over an empty
            // pane would stay blank forever without this escape.
            if (import.meta.env.VITE_IS_SIDECAR_BUILD ||
                import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD) {
                options?.closeCallback?.();
            }
        }
    }, [$t, isUserLoggedIn, openModal, openVisitorLoginUpsell, redirectBasePath, subject]);
    return useMemo(() => ({
        handleRequestAccess,
        isRequestAccessReady: !isUserLoggedInLoading,
    }), [handleRequestAccess, isUserLoggedInLoading]);
}
const getSafeSameOriginRedirectUrl = (redirectUrl, fallbackRedirectUrl) => {
    if (!redirectUrl) {
        return fallbackRedirectUrl;
    }
    try {
        const publicWebOrigin = getWindowOrigin({ intent: 'public-web' });
        const parsedUrl = new URL(redirectUrl, publicWebOrigin);
        if (parsedUrl.origin === publicWebOrigin) {
            return parsedUrl.href;
        }
    }
    catch {
        logger.warn('Ignoring invalid request access redirect URL');
    }
    return fallbackRedirectUrl;
};
//# sourceMappingURL=useRequestAccess.js.map